import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef } from 'react';
import Decimal from 'decimal.js';
import { FormProvider, useForm } from 'react-hook-form';
import { Button, CloseButton, Column, Popover, Row, useDevice, useStateInRef } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { outputToCost, serviceToInput } from '~/components/ServiceBuilder/ServiceBuilder.utils';
import { useScrollBorder } from '~/hooks/useScrollBorder';
import { serviceModel } from '~/models';
import { isAwaitingApproval } from '~/models/service/selectors';
import { inputBillingDataToModel } from '~/models/service-billing/setters';
import { handleDiscountExpiration } from '../EditAgreementModal/utils/EditAgreementModal.utils';
import styles from './ServicePriceEditorPopover.module.scss';
import ServicePriceEditorPopoverBundleContent from './ServicePriceEditorPopoverBundleContent/ServicePriceEditorPopoverBundleContent';
import ServicePriceEditorPopoverPricingContent from './ServicePriceEditorPopoverPricingContent/ServicePriceEditorPopoverPricingContent';
const BASE_FORM_WIDTH = 368;
const CHAR_WIDTH_ESTIMATE = 8;
const SUFFIX_EXTRA_WIDTH = 40;
const MAX_UNIT_NAME_LENGTH_FOR_BASE_WIDTH = 4;
const MAX_UNIT_NAME_LENGTH_FOR_EXPANSION = 20;
export const getServicePriceEditorPopoverTID = ()=>getTestIds();
const ServicePriceEditorPopover = (props)=>{
    const { serviceWithChanges, editorAnchor, isOpen, setIsOpen, serviceOrigin } = props;
    const getProps = useStateInRef(props);
    const { ServicePriceEditorPopoverTID } = useMemo(()=>getServicePriceEditorPopoverTID(), []);
    const isBundle = serviceModel.isBundle(serviceWithChanges);
    const popoverContentRef = useRef(null);
    const { isMobileDevice } = useDevice();
    const { headerClassName, footerClassName } = useScrollBorder({
        contentRef: popoverContentRef,
        enabled: isOpen,
        headerBaseClassName: styles.header,
        footerBaseClassName: styles.actions
    }, [
        serviceWithChanges,
        isOpen
    ]);
    const form = useForm({
        mode: 'all',
        defaultValues: serviceToInput({
            service: serviceWithChanges
        })
    });
    useEffect(()=>{
        form.reset(serviceToInput({
            service: serviceWithChanges
        }));
    }, [
        serviceWithChanges,
        form
    ]);
    const [unitName, pricing] = form.watch([
        'unitName',
        'pricing'
    ]);
    const formWidth = useMemo(()=>{
        if (isMobileDevice) {
            return '100%';
        }
        if (pricing === 'unit' && unitName) {
            const unitNameLength = unitName.length;
            if (unitNameLength > MAX_UNIT_NAME_LENGTH_FOR_BASE_WIDTH) {
                const cappedLength = Math.min(unitNameLength, MAX_UNIT_NAME_LENGTH_FOR_EXPANSION);
                const additionalWidth = cappedLength * CHAR_WIDTH_ESTIMATE + SUFFIX_EXTRA_WIDTH;
                return BASE_FORM_WIDTH + additionalWidth;
            }
        }
        return BASE_FORM_WIDTH;
    }, [
        unitName,
        pricing,
        isMobileDevice
    ]);
    const onSave = (data)=>{
        const { service, onServiceChange } = getProps();
        const cost = outputToCost({
            output: data,
            originalService: service
        });
        let updatedService = service;
        if (isBundle && data.bundle && data.bundle.length > 0) {
            const includedServiceRates = data.bundle.reduce((acc, includedService)=>{
                if (includedService.serviceTemplateId) acc[includedService.serviceTemplateId] = includedService.rate.toNumber();
                return acc;
            }, {});
            const bundleRate = service.cost.pricingType === 'fixed' || service.cost.pricingType === 'variable' ? service.cost.price : new Decimal(0);
            const discountAmount = service.cost.discount ? service.cost.discount.amount() : null;
            updatedService = serviceModel.setBundlePrice(service, includedServiceRates, bundleRate, discountAmount);
        }
        // Update billing trigger based on form data
        const newBilling = inputBillingDataToModel({
            billingForm: data,
            originalService: service
        });
        if (isAwaitingApproval(service.status)) {
            updatedService = serviceModel.setCost(updatedService, cost);
            updatedService = serviceModel.setBillingTrigger(updatedService, newBilling);
        } else {
            updatedService = serviceModel.setCostChanges(updatedService, cost);
            updatedService = handleDiscountExpiration(updatedService, service);
            updatedService = serviceModel.setBillingChanges(updatedService, newBilling);
        }
        onServiceChange(updatedService);
        setIsOpen(false);
    };
    const onDismiss = ()=>{
        setIsOpen(false);
        setTimeout(form.reset, 100);
    };
    return /*#__PURE__*/ _jsx(Popover, {
        classes: {
            paper: styles.popover
        },
        open: isOpen,
        bottomSheetHeader: {
            title: serviceWithChanges.name
        },
        anchorEl: editorAnchor,
        onClose: onDismiss,
        testId: ServicePriceEditorPopoverTID.Root(),
        children: /*#__PURE__*/ _jsx(FormProvider, _object_spread_props(_object_spread({}, form), {
            children: /*#__PURE__*/ _jsx("form", {
                onSubmit: form.handleSubmit(onSave),
                className: styles.form,
                style: {
                    width: formWidth
                },
                children: /*#__PURE__*/ _jsxs(Column, {
                    className: styles.formColumn,
                    style: {
                        width: formWidth
                    },
                    children: [
                        !isMobileDevice && /*#__PURE__*/ _jsx(Row, {
                            justifyContent: "end",
                            className: headerClassName,
                            children: /*#__PURE__*/ _jsx(CloseButton, {
                                onClick: onDismiss,
                                testId: ServicePriceEditorPopoverTID.CloseButton()
                            })
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            ref: popoverContentRef,
                            className: styles.popoverContent,
                            children: /*#__PURE__*/ _jsxs(Column, {
                                children: [
                                    /*#__PURE__*/ _jsx(ServicePriceEditorPopoverPricingContent, {
                                        serviceOrigin: serviceOrigin
                                    }),
                                    /*#__PURE__*/ _jsx(ServicePriceEditorPopoverBundleContent, {})
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsxs(Row, {
                            gap: 16,
                            className: footerClassName,
                            children: [
                                /*#__PURE__*/ _jsx(Button, {
                                    fluid: true,
                                    type: "button",
                                    skin: "primaryOutline",
                                    onClick: onDismiss,
                                    testId: ServicePriceEditorPopoverTID.CancelButton(),
                                    children: "Cancel"
                                }),
                                /*#__PURE__*/ _jsx(Button, {
                                    fluid: true,
                                    type: "submit",
                                    testId: ServicePriceEditorPopoverTID.SaveButton(),
                                    children: "Save"
                                })
                            ]
                        })
                    ]
                })
            })
        }))
    });
};
export default ServicePriceEditorPopover;
